import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Trash2, 
  Send, 
  Edit3, 
  PlusCircle, 
  RefreshCw, 
  AlertCircle, 
  CheckCircle2, 
  X,
  Save
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

export function UserDraftsList() {
  const { session } = useAuth();
  const navigate = useNavigate();

  const [drafts, setDrafts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  // Edit Modal State
  const [editingDraft, setEditingDraft] = useState<any | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editObjective, setEditObjective] = useState('');
  const [editPriority, setEditPriority] = useState('Medium');
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchDrafts = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = session?.access_token || localStorage.getItem('compass_token');
      const res = await fetch('/api/user/drafts', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to load drafts');
      const data = await res.json();
      if (data.success) {
        setDrafts(data.data || []);
      } else {
        throw new Error(data.error || 'Failed to fetch drafts');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrafts();
  }, [session]);

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`Are you sure you want to delete the draft "${title}"?`)) return;

    try {
      const token = session?.access_token || localStorage.getItem('compass_token');
      const res = await fetch(`/api/user/drafts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to delete draft');

      setActionMsg('Draft deleted successfully.');
      fetchDrafts();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleSubmitDraft = async (id: string) => {
    if (!confirm('Submit this draft into Step 1 (CREATED) of the official 15-stage workflow?')) return;

    try {
      const token = session?.access_token || localStorage.getItem('compass_token');
      const res = await fetch(`/api/user/drafts/${id}/submit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to submit draft');

      setActionMsg(`Use Case ${data.data?.number} created and entered official 15-stage workflow!`);
      setTimeout(() => {
        navigate('/user/use-cases');
      }, 1200);
    } catch (e: any) {
      alert(e.message);
    }
  };

  const openEdit = (draft: any) => {
    setEditingDraft(draft);
    setEditTitle(draft.title || '');
    setEditDescription(draft.description || '');
    setEditObjective(draft.detection_objective || '');
    setEditPriority(draft.priority || 'Medium');
  };

  const saveEdit = async () => {
    if (!editingDraft) return;
    try {
      setSavingEdit(true);
      const token = session?.access_token || localStorage.getItem('compass_token');
      const res = await fetch(`/api/user/use-cases/${editingDraft.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          title: editTitle,
          description: editDescription,
          detection_objective: editObjective,
          priority: editPriority
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to update draft');

      setEditingDraft(null);
      fetchDrafts();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="space-y-6 w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <FileText className="h-5 w-5 text-slate-400" />
            <span>My Use Case Drafts</span>
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Work-in-progress detection rules. Drafts are isolated and do not enter the official 15-stage workflow until submitted.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchDrafts}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-card hover:bg-muted text-foreground transition-colors"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => navigate('/user/use-cases/create')}
            className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            <PlusCircle className="h-3.5 w-3.5" />
            <span>New Use Case</span>
          </button>
        </div>
      </div>

      {actionMsg && (
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 text-xs flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span>{actionMsg}</span>
        </div>
      )}

      {error && (
        <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 text-xs flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* DRAFTS LIST */}
      <div className="rounded-xl border border-border bg-card shadow-xs overflow-hidden">
        {loading ? (
          <div className="p-16 flex flex-col items-center justify-center text-muted-foreground gap-3">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
            <span className="text-xs">Loading saved drafts...</span>
          </div>
        ) : drafts.length === 0 ? (
          <div className="p-16 text-center text-muted-foreground">
            <FileText className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm font-medium text-foreground">No drafts saved</p>
            <p className="text-xs mt-1">When creating a Use Case, click "Save Draft" to stage your work without entering the official workflow.</p>
            <button
              onClick={() => navigate('/user/use-cases/create')}
              className="mt-4 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              Start New Draft
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/40 text-muted-foreground uppercase text-[10px] tracking-wider border-b border-border">
                <tr>
                  <th className="py-3 px-4 font-semibold">Draft Identifier</th>
                  <th className="py-3 px-4 font-semibold">Title</th>
                  <th className="py-3 px-4 font-semibold">Priority</th>
                  <th className="py-3 px-4 font-semibold">Last Saved</th>
                  <th className="py-3 px-4 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {drafts.map((d) => (
                  <tr key={d.id} className="hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-4 font-mono font-medium text-slate-400">
                      {d.number}
                    </td>
                    <td className="py-3 px-4 font-medium text-foreground max-w-xs truncate">
                      {d.title}
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-muted text-foreground border border-border">
                        {d.priority}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground whitespace-nowrap">
                      {new Date(d.updated_at).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(d)}
                          className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-md border border-border bg-card hover:bg-muted text-foreground transition-colors"
                          title="Edit draft"
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                          <span>Edit</span>
                        </button>

                        <button
                          onClick={() => handleDelete(d.id, d.title)}
                          className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-md border border-rose-500/30 bg-rose-500/10 hover:bg-rose-500 text-rose-500 hover:text-white transition-colors"
                          title="Delete draft"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          <span>Delete</span>
                        </button>

                        <button
                          onClick={() => handleSubmitDraft(d.id)}
                          className="flex items-center gap-1 px-3 py-1 text-[11px] font-semibold rounded-md bg-blue-600 hover:bg-blue-700 text-white shadow-xs transition-colors"
                          title="Submit into official workflow"
                        >
                          <Send className="h-3.5 w-3.5" />
                          <span>Submit</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* EDIT MODAL */}
      {editingDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
          <div className="relative w-full max-w-xl rounded-2xl border border-border bg-card p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div>
                <h3 className="text-sm font-bold text-foreground">Edit Use Case Draft</h3>
                <span className="text-[11px] font-mono text-muted-foreground">{editingDraft.number}</span>
              </div>
              <button onClick={() => setEditingDraft(null)} className="h-7 w-7 flex items-center justify-center rounded-lg hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold mb-1">Title</label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background"
                />
              </div>

              <div>
                <label className="block font-semibold mb-1">Priority</label>
                <select
                  value={editPriority}
                  onChange={(e) => setEditPriority(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background"
                >
                  <option value="Urgent">Urgent / Critical</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>

              <div>
                <label className="block font-semibold mb-1">Detection Objective</label>
                <input
                  type="text"
                  value={editObjective}
                  onChange={(e) => setEditObjective(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background"
                />
              </div>

              <div>
                <label className="block font-semibold mb-1">Description</label>
                <textarea
                  rows={3}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
              <button
                onClick={() => setEditingDraft(null)}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-border hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                disabled={savingEdit}
                className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                <Save className="h-3.5 w-3.5" />
                <span>{savingEdit ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
